import api from '@/lib/axios';

import type { PaginatedResponse } from '@/types/tenant';
import type { ListFilterPayload } from '@/types/filters';
import type {
  CreatePlatformUserPayload,
  CurrentUserDto,
  PlatformUserDto,
  UpdatePlatformUserPayload,
} from '@/types/user';

// ============================================
// Query Params
// ============================================

interface GetUsersParams {
  page?: number;
  size?: number;
  sort?: string;
}

// ============================================
// Platform Users
// ============================================

export const getUsers = async (
  params: GetUsersParams = {},
  body?: ListFilterPayload,
): Promise<PaginatedResponse<PlatformUserDto>> => {
  const response = await api.post<PaginatedResponse<PlatformUserDto>>(
    '/platform-service/v1/users/list',
    body ?? {},
    { params },
  );
  return response.data;
};

export const createUser = async (data: CreatePlatformUserPayload): Promise<PlatformUserDto> => {
  const response = await api.post<PlatformUserDto>('/platform-service/v1/users', data, {
    successToast: true,
  });
  return response.data;
};

export const getUserById = async (id: string): Promise<PlatformUserDto> => {
  const response = await api.get<PlatformUserDto>(`/platform-service/v1/users/${id}`);
  return response.data;
};

export const updateUser = async (
  id: string,
  data: UpdatePlatformUserPayload,
): Promise<PlatformUserDto> => {
  const response = await api.put<PlatformUserDto>(`/platform-service/v1/users/${id}`, data, {
    successToast: true,
  });
  return response.data;
};

// ============================================
// Current User (self lookup)
// ============================================

export const getCurrentUser = async (): Promise<CurrentUserDto> => {
  const response = await api.get<CurrentUserDto>('/platform-service/v1/users/me');
  return response.data;
};

// ============================================
// Profile Image
// ============================================

/**
 * Sets the user's avatar. `path` is the bucket-prefixed GCS path produced by
 * file-management-service (the upload response's `bucketPath` field) — posted
 * verbatim. Backend regex-validates `^(/[A-Za-z0-9_][A-Za-z0-9._\-]*){2,}$`.
 */
export const setUserProfileImage = async (id: string, path: string): Promise<PlatformUserDto> => {
  const response = await api.put<PlatformUserDto>(
    `/platform-service/v1/users/${id}/profile-image`,
    { path },
    { successToast: true },
  );
  return response.data;
};

export const clearUserProfileImage = async (id: string): Promise<PlatformUserDto> => {
  const response = await api.delete<PlatformUserDto>(
    `/platform-service/v1/users/${id}/profile-image`,
    { successToast: true },
  );
  return response.data;
};

export const inactivateUser = async (id: string): Promise<PlatformUserDto> => {
  const response = await api.post<PlatformUserDto>(
    `/platform-service/v1/users/${id}/inactivate`,
    {},
    { successToast: true },
  );
  return response.data;
};
